import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Info, ExternalLink, Search, X, ZoomIn, Save } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import ZoomableImage from '../components/ZoomableImage';

export default function Parts() {
    const location = useLocation();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [vehicleContext, setVehicleContext] = useState<any>(null);
    const { user, login } = useAuth();

    useEffect(() => {
        const state = location.state as any;
        const initialSearch = state?.initialSearch;
        const context = state?.vehicleContext;

        if (context) setVehicleContext(context);

        if (initialSearch) {
            setQuery(initialSearch);
            performSearch(initialSearch, context);
        }
    }, [location.state]);

    const performSearch = async (searchQuery: string, context?: any) => {
        setLoading(true);
        try {
            const data = await api.searchParts(searchQuery, context || vehicleContext || {}, 100);
            setResults(data.parts || data.results || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        if (!query.trim()) return;
        performSearch(query);
    };

    return (
        <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-4">
                    <h2 className="text-3xl font-black text-white uppercase tracking-tighter">Parts Registry</h2>
                    <div className="flex gap-3">
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                placeholder="PN#, Model, or Component descriptor..."
                                className="w-full bg-dark border border-slate/30 rounded-xl px-4 py-4 text-white focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none"
                            />
                            <Search className="absolute right-4 top-4 text-gray-500" />
                        </div>
                        <button
                            onClick={handleSearch}
                            disabled={loading || !query.trim()}
                            className="bg-primary text-dark px-10 rounded-xl font-bold uppercase tracking-widest hover:bg-primary/90 transition-all disabled:opacity-50"
                        >
                            {loading ? 'Searching...' : 'Scan'}
                        </button>
                        <button
                            onClick={() => {
                                if (!user) {
                                    const email = prompt('Sign in to save searches. Enter your email:');
                                    if (email) login(email);
                                    return;
                                }
                                if (results.length === 0) return;
                                api.saveProject({
                                    id: Date.now().toString(),
                                    name: query,
                                    type: 'parts',
                                    data: { results },
                                    vehicleContext
                                });
                                alert('Search results saved to Projects.');
                            }}
                            disabled={results.length === 0}
                            title="Save Search"
                            className="p-4 bg-dark border border-slate/30 rounded-xl text-gray-500 hover:text-primary hover:border-primary/50 transition-all disabled:opacity-30"
                        >
                            <Save size={20} />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {results.map((part, i) => (
                        <div key={i} className="bg-dark/40 border border-slate/20 rounded-2xl overflow-hidden group hover:border-primary/30 transition-all flex flex-col">
                            <div className="aspect-video bg-black/50 flex items-center justify-center p-4 relative overflow-hidden">
                                {part.image_url || (part.images && part.images[0]) ? (
                                    <>
                                        <img
                                            src={part.image_url || part.images[0]}
                                            alt={part.name}
                                            className="max-h-full object-contain cursor-zoom-in transition-transform duration-500 group-hover:scale-110"
                                            onClick={() => setSelectedImage(part.image_url || part.images[0])}
                                        />
                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => setSelectedImage(part.image_url || part.images[0])}
                                                className="p-1.5 bg-black/60 rounded-lg text-white hover:bg-primary hover:text-dark transition-colors"
                                            >
                                                <ZoomIn size={16} />
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <Info size={48} className="text-gray-700" />
                                )}
                            </div>
                            <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
                                <div>
                                    <h3 className="text-white font-bold tracking-tight line-clamp-2 min-h-[3rem]">{part.name || 'Component-ID'}</h3>
                                    <p className="text-xs text-gray-500 uppercase font-mono mt-1">{part.source || 'Global Archive'}</p>
                                </div>
                                <div className="flex justify-between items-center pt-2">
                                    <span className="text-2xl font-black text-accent">
                                        {typeof part.price === 'number' ? `$${part.price.toFixed(2)} ` : (part.price || '$--.--')}
                                    </span>
                                    {(part.affiliate_url || part.url) && (
                                        <a
                                            href={part.affiliate_url || part.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-2 bg-slate/20 rounded-lg text-primary hover:bg-primary hover:text-dark transition-all flex items-center gap-2"
                                        >
                                            <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:inline">View Deal</span>
                                            <ExternalLink size={18} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {!loading && query && results.length === 0 && (
                        <div className="col-span-full py-20 text-center text-gray-500 italic">
                            No matching components found in active registry.
                        </div>
                    )}
                </div>
            </div>

            {/* Image Zoom Portal & Ghosting Engine */}
            {selectedImage && (
                <div className="fixed inset-0 z-[100] bg-dark/95 backdrop-blur-2xl flex items-center justify-center p-4 sm:p-12 animate-in fade-in duration-300">
                    <button
                        className="absolute top-8 right-8 z-[110] text-gray-400 hover:text-white transition-colors"
                        onClick={() => setSelectedImage(null)}
                    >
                        <X size={32} />
                    </button>

                    <div className="w-full max-w-6xl animate-in zoom-in-95 duration-500">
                        <ZoomableImage src={selectedImage} />
                    </div>
                </div>
            )}
        </div>
    );
}
